import { Component, OnInit, OnDestroy, ViewChild, TemplateRef } from '@angular/core';

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormBuilder, Validators } from '@angular/forms';

import { SettingsService } from '@services/settings.service';
import { ModalService } from '@services/support/modal.service';
import { FlashMessagesService } from '@mobilestar/ngx-flash-messages';

import { Subscription } from 'rxjs';
import { AccountService } from '@services/account.service';
import { first } from 'rxjs/operators';
import { LoaderService } from '@services/support/loader.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit, OnDestroy {

  @ViewChild("addTemplate", { static: true }) private addTemplate: TemplateRef<any>;
  @ViewChild("changeTemplate", { static: true }) private changeTemplate: TemplateRef<any>;
  @ViewChild("sendDemoTemplate", { static: true }) private sendDemoTemplate: TemplateRef<any>;

  updates: any[] = null;
  blog: any[] = null;

  updatesSub: Subscription = null;
  blogSub: Subscription = null;

  constructor(
    private accountService: AccountService,
    private settingsService: SettingsService,
    private modalService: ModalService,
    private flashMessages: FlashMessagesService,
    private loaderService: LoaderService,
    private formBuilder: FormBuilder
  ) { }

  ngOnInit() {
    this.updatesSub = this.settingsService.getUpdates().subscribe(updates => {
      this.updates = updates;
    });

    this.blogSub = this.settingsService.getBlog().subscribe(blog => {
      this.blog = blog;
    });
  }

  ngOnDestroy() {
    if (this.updatesSub) { this.updatesSub.unsubscribe() }
    if (this.blogSub) { this.blogSub.unsubscribe() }
  }

  async sendDemoMail() {
    const accounts = await this.accountService.accountsMeta.pipe(first()).toPromise();

    const form = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(/^$|^\w+([.-]?\w+)*@\w+([.-]?\w+)*(.\w{2,})+$/)]],
      account: accounts[0].id
    });
    const modalId = this.modalService.show({
      template: this.sendDemoTemplate,
      context: {
        dataModel: { form: form, accounts: accounts },
        callbacks: {
          close: () => this.modalService.hide(modalId),
          send: () => {
            this.loaderService.show();
            this.settingsService.sendDemoMail(form.value.email, form.value.account)
            .then(() => {
              this.flashMessages.show("Usage mail has been sent to your email address.", { cssClass: 'alert-success', timeout: 4000, closeOnClick: true });
              this.modalService.hide(modalId);
            })
            .catch(error => this.flashMessages.show("Cannot send mail.", { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true }))
            .finally(() => {
              this.loaderService.hide();
            }); 
          }
        }
      }
    });
  }

  addNew(type: string) {
    const form = this.formBuilder.group({
      text: ['', [Validators.required]],
      url: ['', [Validators.required]],
    });
    const modalId = this.modalService.show({
      template: this.addTemplate,
      context: {
        dataModel: { form: form },
        callbacks: {
          close: () => this.modalService.hide(modalId),
          add: () => {
            if (type === 'update') {
              this.settingsService.createUpdate(form.value)
              .then(() => this.modalService.hide(modalId))
              .catch(error => this.flashMessages.show("Cannot add update.", { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true }));  
            } else if (type === 'post') {
              this.settingsService.createPost(form.value)
              .then(() => this.modalService.hide(modalId))
              .catch(error => this.flashMessages.show("Cannot add post.", { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true }));  
            }
          }
        }
      }
    });
  }

  changeEmailData(type: string, data: any) {
    const form = this.formBuilder.group({
      text: [data.text, [Validators.required]],
      url: [data.url, [Validators.required]],
    });
    const modalId = this.modalService.show({
      template: this.changeTemplate,
      context: {
        dataModel: { form: form },
        callbacks: {
          close: () => this.modalService.hide(modalId),
          change: () => {
            if (type === 'update') {
              this.settingsService.changeUpdate(data, form.value)
              .then(() => this.modalService.hide(modalId))
              .catch(error => this.flashMessages.show("Cannot add update.", { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true }));  
            } else if (type === 'post') {
              this.settingsService.changePost(data, form.value)
              .then(() => this.modalService.hide(modalId))
              .catch(error => this.flashMessages.show("Cannot add post.", { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true }));  
            }
          }
        }
      }
    });
  }

  updateDropped(event: CdkDragDrop<any[]>) {
    this.settingsService.moveUpdate(this.updates[event.previousIndex].id, event.currentIndex);
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
  }

  postDropped(event: CdkDragDrop<any[]>) {
    this.settingsService.movePost(this.blog[event.previousIndex].id, event.currentIndex);
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
  }

  deleteUpdate(updateId: string) {
    this.settingsService.deleteUpdate(updateId);    
  }

  deletePost(postId: string) {
    this.settingsService.deletePost(postId);
  }
}
